// ─── Text blocks on the feedback canvas ──────────────────────────
//
// The canvas's fifth element kind (f-01954): a panel of RICH text on the board.
// Everything here is pure and DOM-free, for the same reason feedback-link.ts is —
// the unit runner can cover it, and the sanitizer at the bottom of this file is a
// security property rather than a convenience.
//
// How a text block differs from the sticky note it is easily confused with:
//
//   · The note's text is PLAIN and auto-shrinks until it fits its square. A text
//     block's text is rich, keeps the size it was given, and CLIPS at the edge of
//     the box — because a block is where a paragraph goes, and a paragraph that
//     silently shrank to 7pt to fit would be unreadable rather than accommodated.
//   · The note is one colour with one legible ink. A block has a background AND a
//     border, both chosen separately, and per-character colours inside it.
//   · A note holds 2,000 characters. A block holds 128k.
//
// The two palettes below are ABSOLUTE — unlike the note fills, they do NOT flip
// between light and dark. That is forced, not a preference: a block's ink is stored
// per character inside its HTML, so if the paper moved under it when someone
// switched to dark mode, hand-picked black text would land on a near-black block
// and vanish. The paper stays where it was put; each fill therefore ships with the
// default ink that reads on it.
/** A freshly placed block: a comfortable reading measure at the zoom boards are
 *  worked at, and tall enough for a short paragraph. Resizing is free from then
 *  on — both axes independent, like a note and unlike a screenshot. */
export const FEEDBACK_TEXT_DEFAULT_W = 420;
export const FEEDBACK_TEXT_DEFAULT_H = 240;
/** Smallest a block can be dragged to. Below this the handles overlap and the
 *  padding leaves no line box at all. */
export const FEEDBACK_TEXT_MIN_W = 80;
export const FEEDBACK_TEXT_MIN_H = 48;
/**
 * The text ceiling, as the request words it: "up to 128k text in a text block".
 *
 * Counted in characters of TEXT, not of markup — so a block full of headings and
 * links holds as much writing as a block of bare paragraphs. `sanitizeFeedbackRichText`
 * stops emitting once this is reached and closes what it had open, so the result is
 * always well-formed rather than a truncated tag.
 */
export const FEEDBACK_TEXT_MAX = 128 * 1024;
/**
 * The separate ceiling on the MARKUP, which the text budget alone does not bound:
 * a megabyte of empty `<span>`s carries no text at all. Generous enough that no
 * real document meets it — 128k of text at four bytes of tagging per character —
 * and low enough that a markup bomb cannot become a row, a websocket frame and a
 * repaint.
 */
export const FEEDBACK_TEXT_HTML_MAX = 512 * 1024;
// ─── size, in points ─────────────────────────────────────────────
//
// POINTS are the authoring unit — the unit a person names a text size in, and
// what the toolbar's field shows and steps (f-01958). WORLD PIXELS remain the
// stored unit, because that is what the canvas draws in, what the sanitizer
// validates, and what a paste out of a browser already carries. So there is one
// internal unit and one spoken unit, converted only at the toolbar's edge.
/** Points per world pixel and back. 1pt = 1/72in, 1 CSS px = 1/96in. */
const PX_PER_PT = 96 / 72;
/** A new block's type size. 12pt is what "default text size" means to a person,
 *  and it lands on exactly 16 world px — so this is a NAMING change, not a
 *  resizing one, and no block already on a board reflows. */
export const FEEDBACK_TEXT_DEFAULT_PT = 12;
/** What the size field accepts: 1pt to 64pt, stepping by one (b-01960). Wider at
 *  the bottom than a word processor offers, because a text block on a zoomable
 *  canvas is read at whatever zoom you like — 1pt is legible at 800%. */
export const FEEDBACK_TEXT_PT_MIN = 1;
export const FEEDBACK_TEXT_PT_MAX = 64;
/** The stepper's step. One point, as every word processor does it. */
export const FEEDBACK_TEXT_PT_STEP = 1;
export function ptToWorldPx(pt) {
    return Math.round(pt * PX_PER_PT * 10) / 10;
}
/**
 * World px → points, to one decimal.
 *
 * NOT rounded to a whole number, which is what this first did: a size that is
 * genuinely fractional — 13.5px out of a Word paste is 10.1pt — should say so
 * rather than claim to be 10. Stepping from it still lands on a whole number,
 * because that is the stepper's job (see `stepPt`), so nothing has to settle.
 *
 * One decimal, and the trailing `.0` is dropped by `Number`, so a whole size
 * displays as `12` and never `12.0`.
 */
export function worldPxToPt(px) {
    return Math.round((px / PX_PER_PT) * 10) / 10;
}
/**
 * One step of the size field, in points.
 *
 * From a whole number this is just ±1. From a FRACTIONAL size it goes to the next
 * whole number in the direction of travel — up from 11.4 is 12, down from 11.4 is
 * 11 — rather than 12.4 and 10.4, which would keep the fraction forever. Done here
 * rather than left to `input.stepUp()` because the native stepper's snapping is
 * defined against the `min`/`step` grid, and a value off that grid also makes the
 * field report itself invalid; owning the arithmetic avoids both.
 */
export function stepPt(pt, direction) {
    const from = Number.isFinite(pt) ? pt : FEEDBACK_TEXT_DEFAULT_PT;
    const next = Number.isInteger(from)
        ? from + direction
        : (direction === 1 ? Math.ceil(from) : Math.floor(from));
    return clampPt(next);
}
/**
 * Clamp a point size into the field's range.
 *
 * Deliberately does NOT round: a fractional size is legal and is shown as one, so
 * clamping 10.1 must give 10.1 back rather than 10. Only the bounds are enforced.
 */
export function clampPt(pt) {
    if (!Number.isFinite(pt))
        return FEEDBACK_TEXT_DEFAULT_PT;
    const bounded = Math.min(FEEDBACK_TEXT_PT_MAX, Math.max(FEEDBACK_TEXT_PT_MIN, pt));
    return Math.round(bounded * 10) / 10;
}
/** The block's own base type size, in world units (the canvas scales it like
 *  everything else). Sizes chosen inside the block are stored per run. */
export const FEEDBACK_TEXT_BASE_SIZE = ptToWorldPx(FEEDBACK_TEXT_DEFAULT_PT);
/**
 * What a stored `font-size` is clamped to, in world px.
 *
 * DELIBERATELY WIDER than the pt field's range: this is the sanitizer's safety
 * bound, and it also has to admit whatever a paste out of Word or a web page
 * carries — which is on nobody's point grid. Anything outside is dropped, so the
 * run falls back to the block's base size rather than painting a 4,000px glyph
 * that swallows the board.
 */
// 1pt is 1.3px, so the floor has to admit it or the smallest size the field
// offers would be silently dropped on save (b-01960).
export const FEEDBACK_TEXT_SIZE_MIN = 1;
export const FEEDBACK_TEXT_SIZE_MAX = 256;
/**
 * The leading ladder, labelled by what it actually applies — so the dropdown never
 * says one number and does another. Word's ladder, minus its habit of calling 1.15
 * "Single".
 */
export const FEEDBACK_TEXT_LEADINGS = [
    { key: 'normal', label: 'Normal', css: 'normal' },
    { key: '1.15', label: '1.15', css: '1.15' },
    { key: '1.25', label: '1.25', css: '1.25' },
    { key: '1.5', label: '1.5', css: '1.5' },
    { key: '2', label: 'Double (2.0)', css: '2' },
    { key: '3', label: 'Triple (3.0)', css: '3' },
];
/** A new block's leading: the font's own. Everything else on the ladder is looser
 *  than the type was designed to be set at, so it is what a block should start as. */
export const FEEDBACK_TEXT_DEFAULT_LEADING = 'normal';
export function feedbackTextLeadingDef(key) {
    return FEEDBACK_TEXT_LEADINGS.find((l) => l.key === key)
        ?? FEEDBACK_TEXT_LEADINGS.find((l) => l.key === FEEDBACK_TEXT_DEFAULT_LEADING);
}
/**
 * The block's paper. Twelve, light through dark, plus a transparent one — which
 * earns its place on a canvas: a block with no paper is how you write over a
 * screenshot without covering it.
 *
 * `white` is FIRST and is the default, per the request. Keys are stable slugs:
 * a label may be reworded, the stored value never changes.
 */
export const FEEDBACK_TEXT_FILLS = [
    { key: 'white', label: 'White', token: '--fbt-white' },
    { key: 'ivory', label: 'Ivory', token: '--fbt-ivory' },
    { key: 'sand', label: 'Sand', token: '--fbt-sand' },
    { key: 'blush', label: 'Blush', token: '--fbt-blush' },
    { key: 'rose', label: 'Rose', token: '--fbt-rose' },
    { key: 'lilac', label: 'Lilac', token: '--fbt-lilac' },
    { key: 'sky', label: 'Sky', token: '--fbt-sky' },
    { key: 'mint', label: 'Mint', token: '--fbt-mint' },
    { key: 'sage', label: 'Sage', token: '--fbt-sage' },
    { key: 'stone', label: 'Stone', token: '--fbt-stone' },
    { key: 'slate', label: 'Slate', token: '--fbt-slate' },
    { key: 'ink', label: 'Ink', token: '--fbt-ink' },
    { key: 'none', label: 'No fill', token: '--fbt-none' },
];
export const FEEDBACK_TEXT_DEFAULT_FILL = 'white';
/** The block's frame. `black` is FIRST and is the default — "a thin black border
 *  and white background" is the shape the request asks for. `none` is offered
 *  because a filled block often reads better without one. */
export const FEEDBACK_TEXT_BORDERS = [
    { key: 'black', label: 'Black', token: '--fbt-edge-black' },
    { key: 'grey', label: 'Grey', token: '--fbt-edge-grey' },
    { key: 'slate', label: 'Slate', token: '--fbt-edge-slate' },
    { key: 'red', label: 'Red', token: '--fbt-edge-red' },
    { key: 'amber', label: 'Amber', token: '--fbt-edge-amber' },
    { key: 'green', label: 'Green', token: '--fbt-edge-green' },
    { key: 'teal', label: 'Teal', token: '--fbt-edge-teal' },
    { key: 'blue', label: 'Blue', token: '--fbt-edge-blue' },
    { key: 'violet', label: 'Violet', token: '--fbt-edge-violet' },
    { key: 'none', label: 'No border', token: '--fbt-edge-none' },
];
export const FEEDBACK_TEXT_DEFAULT_BORDER = 'black';
/** A stored fill → its definition, falling back to the default so an unknown slug
 *  (an older row, a hand-written API call) still paints. */
export function feedbackTextFillDef(key) {
    return FEEDBACK_TEXT_FILLS.find((c) => c.key === key) ?? FEEDBACK_TEXT_FILLS[0];
}
export function feedbackTextBorderDef(key) {
    return FEEDBACK_TEXT_BORDERS.find((c) => c.key === key) ?? FEEDBACK_TEXT_BORDERS[0];
}
/**
 * The ink and highlight swatches the formatting toolbar offers.
 *
 * Literal hex rather than tokens, because unlike the paper these values are
 * written INTO the block's HTML, one run at a time — a token would resolve
 * differently on the next theme and silently rewrite text somebody had coloured.
 * A stored colour has to mean exactly what it said when it was picked.
 */
export const FEEDBACK_TEXT_INKS = [
    { key: 'default', label: 'Default', css: '' },
    { key: 'black', label: 'Black', css: '#111111' },
    { key: 'grey', label: 'Grey', css: '#6b7280' },
    { key: 'red', label: 'Red', css: '#c0392b' },
    { key: 'orange', label: 'Orange', css: '#c2610c' },
    { key: 'green', label: 'Green', css: '#1f7a3d' },
    { key: 'teal', label: 'Teal', css: '#0f7a72' },
    { key: 'blue', label: 'Blue', css: '#1f5fd0' },
    { key: 'violet', label: 'Violet', css: '#6d3fc4' },
    { key: 'white', label: 'White', css: '#ffffff' },
];
export const FEEDBACK_TEXT_HIGHLIGHTS = [
    { key: 'none', label: 'None', css: '' },
    { key: 'yellow', label: 'Yellow', css: '#fff3a8' },
    { key: 'lime', label: 'Lime', css: '#dcf7a8' },
    { key: 'mint', label: 'Mint', css: '#b6f2cd' },
    { key: 'sky', label: 'Sky', css: '#c2e8ff' },
    { key: 'lilac', label: 'Lilac', css: '#e2d6fb' },
    { key: 'pink', label: 'Pink', css: '#ffd0e4' },
    { key: 'peach', label: 'Peach', css: '#ffdcc2' },
    { key: 'grey', label: 'Grey', css: '#e3e6ea' },
];
// ═════════════════════════════════════════════════════════════════
// The sanitizer
// ═════════════════════════════════════════════════════════════════
//
// A text block's content is HTML written by one person and rendered to everyone
// else who can open the board, so it is treated as hostile input no matter who
// typed it: a paste out of a web page, a compromised account, a hand-written API
// call. Both ends run this ONE function — the API on every write, which is the
// durable guarantee, and the canvas on read and on paste, which is what keeps a
// row written before this shipped from painting something the server would now
// refuse. The same discipline `parseLinkPaste` gets, for the same reason: two
// definitions of "safe" would eventually disagree.
//
// It is SAFE BY CONSTRUCTION rather than by filtering. Nothing from the input is
// ever copied through to the output: text is decoded and re-escaped, a tag is
// re-emitted from its own allowlisted name, and an attribute is re-emitted only
// once its value has passed a validator and been escaped. So the failure mode of
// a parser bug is a dropped tag, not an executed one.
//
// @pcp/shared has no DOM, so there is no DOMParser to lean on — which is just as
// well: the server has no DOM either, and a sanitizer that only runs in a browser
// is a sanitizer that does not run where it matters.
/** Elements that survive, re-emitted by name. */
const ALLOWED_TAGS = new Set([
    'p', 'br', 'div', 'span',
    'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del', 'ins', 'mark', 'sub', 'sup',
    'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'h4',
    'blockquote', 'code', 'pre', 'hr', 'a',
]);
/** Elements with no closing tag. Emitted as `<br>`, never `<br></br>`. */
const VOID_TAGS = new Set(['br', 'hr']);
/**
 * Elements dropped along with their whole subtree, because rendering their
 * CONTENT as text would be worse than losing it: nobody wants to read a
 * stylesheet, and nobody should be shown the inside of a `<script>`.
 *
 * `img`, `picture` and `source` are here on purpose and not as a security
 * measure — the request is explicit that a text block is text, and that a paste
 * carrying inline images drops them.
 */
const DROP_SUBTREE = new Set([
    'script', 'style', 'iframe', 'object', 'embed', 'form', 'button', 'select',
    'option', 'textarea', 'noscript', 'template', 'svg', 'math', 'audio', 'video',
    'canvas', 'map', 'frameset', 'frame', 'applet', 'title', 'head',
]);
/** Void elements that are dropped: there is no close tag to suppress up to, so
 *  the tag itself simply never reaches the output. */
const DROP_VOID = new Set([
    'img', 'input', 'source', 'track', 'link', 'meta', 'base', 'param', 'area', 'col',
]);
/** Elements whose content is NOT markup, so `a < b` inside one is text rather
 *  than a tag. Skipped verbatim to their close tag; all of them are in
 *  DROP_SUBTREE, so none of that text is ever emitted. */
const RAW_TEXT = new Set(['script', 'style', 'textarea', 'title']);
/** Per-tag attribute allowlist. Anything absent is stripped — every `on*`
 *  handler, `class`, `id`, `srcset`, and `style` on a tag that has no business
 *  carrying one. */
const STYLEABLE = new Set([
    'span', 'p', 'div', 'li', 'h1', 'h2', 'h3', 'h4', 'strong', 'b', 'em', 'i',
    'u', 's', 'strike', 'del', 'ins', 'mark', 'code', 'blockquote', 'pre', 'a', 'sub', 'sup',
]);
/** CSS properties a `style` attribute may keep. Everything else goes, which is
 *  what keeps `position`, `content`, `background-image` and the rest of the
 *  layout-breaking half of CSS off the board. */
const STYLE_PROPS = new Set([
    'color', 'background-color', 'font-size', 'font-weight', 'font-style',
    'text-decoration', 'text-decoration-line', 'text-align',
]);
/**
 * How deep the tag stack may go. A pasted document nests four or five levels; a
 * nesting bomb nests forty thousand, and every level costs the browser a layout
 * box. Past the limit the element is UNWRAPPED — its text survives, its tag does
 * not — so the content is still readable.
 */
const MAX_DEPTH = 24;
const NAMED_COLORS = new Set([
    'black', 'white', 'red', 'green', 'blue', 'yellow', 'orange', 'purple', 'pink',
    'brown', 'grey', 'gray', 'cyan', 'magenta', 'lime', 'navy', 'teal', 'olive',
    'maroon', 'silver', 'gold', 'beige', 'ivory', 'khaki', 'salmon', 'coral',
    'crimson', 'indigo', 'violet', 'turquoise', 'tan', 'plum', 'orchid', 'lavender',
    'transparent',
]);
/** Named colours are matched against the list above rather than against the full
 *  CSS set of 148: an unrecognised name is dropped, the run inherits the block's
 *  ink, and nothing is lost but a shade nobody picked from a swatch. */
function safeColor(raw) {
    const v = raw.trim().toLowerCase();
    if (!v)
        return null;
    if (/^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(v))
        return v;
    // rgb/rgba/hsl/hsla with numbers, percentages and the modern slash-alpha form.
    // Deliberately no `var()`, no `color-mix()`, no relative colours: those resolve
    // against a theme, and a stored ink has to mean what it said when it was picked.
    if (/^(?:rgb|hsl)a?\(\s*[-\d.]+(?:deg|%)?\s*[, ]\s*[-\d.]+%?\s*[, ]\s*[-\d.]+%?\s*(?:[,/]\s*[-\d.]+%?\s*)?\)$/.test(v))
        return v;
    if (NAMED_COLORS.has(v))
        return v;
    return null;
}
/** A size in world px, clamped. `pt` is converted because that is what a paste out
 *  of Word or Google Docs carries; relative units (`em`, `rem`, `%`) are dropped
 *  rather than resolved, because they compound through nested spans and a run
 *  three levels deep would come out at a size nobody chose. */
function safeFontSize(raw) {
    const m = /^([\d.]+)(px|pt)$/.exec(raw.trim().toLowerCase());
    if (!m)
        return null;
    const n = Number(m[1]);
    if (!Number.isFinite(n) || n <= 0)
        return null;
    const px = m[2] === 'pt' ? n * (96 / 72) : n;
    if (px < FEEDBACK_TEXT_SIZE_MIN || px > FEEDBACK_TEXT_SIZE_MAX)
        return null;
    return `${Math.round(px * 10) / 10}px`;
}
function safeStyle(raw) {
    const out = [];
    for (const chunk of raw.split(';')) {
        const i = chunk.indexOf(':');
        if (i < 0)
            continue;
        const prop = chunk.slice(0, i).trim().toLowerCase();
        // `!important` would let a run outrank the block's own rules; the declaration
        // is kept, the priority is not.
        const value = chunk.slice(i + 1).replace(/!\s*important/i, '').trim();
        if (!STYLE_PROPS.has(prop) || !value)
            continue;
        if (prop === 'color' || prop === 'background-color') {
            const c = safeColor(value);
            if (c)
                out.push(`${prop}:${c}`);
            continue;
        }
        if (prop === 'font-size') {
            const s = safeFontSize(value);
            if (s)
                out.push(`font-size:${s}`);
            continue;
        }
        const v = value.toLowerCase();
        if (prop === 'font-weight') {
            if (/^(?:normal|bold|bolder|lighter|[1-9]00)$/.test(v))
                out.push(`font-weight:${v}`);
            continue;
        }
        if (prop === 'font-style') {
            if (/^(?:normal|italic|oblique)$/.test(v))
                out.push(`font-style:${v}`);
            continue;
        }
        if (prop === 'text-decoration' || prop === 'text-decoration-line') {
            // Only the LINE keywords, and normalised onto one property: the shorthand
            // also carries a colour and a style, and `text-decoration: underline
            // wavy red` is not something a toolbar with four buttons ever produced.
            const words = v.split(/\s+/).filter((w) => /^(?:none|underline|line-through|overline)$/.test(w));
            if (words.length)
                out.push(`text-decoration-line:${words.join(' ')}`);
            continue;
        }
        if (prop === 'text-align') {
            if (/^(?:left|right|center|justify|start|end)$/.test(v))
                out.push(`text-align:${v}`);
        }
    }
    return out.length ? out.join(';') : null;
}
/**
 * Schemes a link inside a block may use.
 *
 * Root-relative paths (`/backlog/1234`) are allowed and stay on this origin —
 * a PCP deep link is exactly what people paste into a board, and localising
 * cross-references is a rule of this codebase rather than an accident. A
 * PROTOCOL-relative `//host/x` is refused: it looks relative and is not.
 */
function safeHref(raw) {
    // Control characters and whitespace are stripped first — `java\tscript:` and
    // `java\nscript:` are how a naive scheme check is beaten.
    let v = '';
    for (const ch of raw.trim()) {
        const code = ch.codePointAt(0) ?? 0;
        if (code > 0x20 && code !== 0x7f)
            v += ch;
    }
    if (!v)
        return null;
    if (v.startsWith('//'))
        return null;
    if (v.startsWith('/') || v.startsWith('#') || v.startsWith('?')) {
        return { href: v, external: false };
    }
    const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(v);
    if (!scheme)
        return null; // no bare relative paths
    const s = scheme[1].toLowerCase();
    if (s === 'http' || s === 'https') {
        return parseHttpUrlish(v) ? { href: v, external: true } : null;
    }
    if (s === 'mailto') {
        return /^mailto:[^\s<>"']{1,320}$/i.test(v) ? { href: v, external: true } : null;
    }
    if (s === 'tel') {
        return /^tel:[+0-9()\-.\s]{1,40}$/i.test(v) ? { href: v, external: true } : null;
    }
    return null;
}
/** Just enough of a URL check to know an http(s) link has a host. Local rather
 *  than imported so this module stays self-contained and testable on its own. */
function parseHttpUrlish(v) {
    const m = /^https?:\/\/([^/?#\s]+)/i.exec(v);
    return !!m && !!m[1] && !m[1].startsWith('.');
}
const ENTITIES = {
    amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
    hellip: '…', mdash: '—', ndash: '–', lsquo: '‘',
    rsquo: '’', ldquo: '“', rdquo: '”', bull: '•',
    middot: '·', copy: '©', reg: '®', trade: '™',
    deg: '°', euro: '€', pound: '£', times: '×',
};
/**
 * Decode entities BEFORE re-escaping, which is the whole point: without it
 * `&amp;` would come back as `&amp;amp;` and grow on every save, and an attribute
 * value could smuggle `&#106;avascript:` past the scheme check.
 */
function decodeEntities(s) {
    if (!s.includes('&'))
        return s;
    return s.replace(/&(#[xX]?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,9});?/g, (whole, body) => {
        if (body.charAt(0) === '#') {
            const hex = body.charAt(1) === 'x' || body.charAt(1) === 'X';
            const n = parseInt(hex ? body.slice(2) : body.slice(1), hex ? 16 : 10);
            if (!Number.isFinite(n) || n <= 0 || n > 0x10ffff)
                return '';
            // Surrogates and the C0/C1 control blocks are dropped rather than emitted:
            // a lone surrogate is not a character, and a control character in a text
            // block is only ever an attempt at something.
            if (n >= 0xd800 && n <= 0xdfff)
                return '';
            if (n < 0x20 && n !== 0x09 && n !== 0x0a)
                return '';
            if (n >= 0x7f && n <= 0x9f)
                return '';
            try {
                return String.fromCodePoint(n);
            }
            catch {
                return '';
            }
        }
        const named = ENTITIES[body.toLowerCase()];
        return named ?? whole;
    });
}
/** Escape for a text node. `<` and `&` are the only ones that must go, but `>`
 *  and the quotes cost nothing and make the output safe to paste anywhere. */
function escapeText(s) {
    let out = '';
    for (const ch of s) {
        if (ch === '&')
            out += '&amp;';
        else if (ch === '<')
            out += '&lt;';
        else if (ch === '>')
            out += '&gt;';
        else
            out += ch;
    }
    return out;
}
/** Escape for a double-quoted attribute value. */
function escapeAttr(s) {
    let out = '';
    for (const ch of s) {
        if (ch === '&')
            out += '&amp;';
        else if (ch === '<')
            out += '&lt;';
        else if (ch === '>')
            out += '&gt;';
        else if (ch === '"')
            out += '&quot;';
        else if (ch === "'")
            out += '&#39;';
        else
            out += ch;
    }
    return out;
}
/**
 * Where a tag ENDS, counting from just after its name.
 *
 * Not `indexOf('>')`, which is what this was first written as and which is wrong
 * in a way that matters: inside a quoted attribute value a `>` is a literal
 * character, so `<a href="data:text/html,<script>alert(1)</script>">` ended the
 * tag at the FIRST `>` — inside the href — and the rest of the href came back
 * out as text with the tag still open. Browsers track the quote state here, so
 * this does too, or the two would disagree about where a tag is.
 *
 * Returns -1 for a truncated tag, which the caller treats as "nothing more to
 * trust" and stops.
 */
function tagEnd(src, from) {
    let quote = '';
    for (let j = from; j < src.length; j++) {
        const ch = src.charAt(j);
        if (quote) {
            if (ch === quote)
                quote = '';
            continue;
        }
        if (ch === '"' || ch === "'") {
            quote = ch;
            continue;
        }
        if (ch === '>')
            return j;
    }
    return -1;
}
/** A `/` that really is a self-closing marker: on its own, or after whitespace.
 *  NOT a trailing slash inside an unquoted attribute value — `<a href=https://x/>`
 *  is an anchor with a path, not an empty element. */
function isSelfClosing(inner) {
    return /(?:^|\s)\/$/.test(inner.trimEnd());
}
/** Attributes off one open tag. Handles quoted, single-quoted and bare values,
 *  and a value-less attribute (`disabled`). */
function parseAttrs(src) {
    const out = [];
    const re = /([^\s/=>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]*)))?/g;
    let m;
    while ((m = re.exec(src))) {
        if (!m[0].trim()) {
            re.lastIndex += 1;
            continue;
        }
        out.push({
            name: m[1].toLowerCase(),
            value: decodeEntities(m[2] ?? m[3] ?? m[4] ?? ''),
        });
        if (out.length > 40)
            break; // no legitimate tag carries forty attributes
    }
    return out;
}
/** The attributes this tag is allowed to keep, already validated and escaped. */
function keepAttrs(tag, attrs) {
    const out = [];
    if (tag === 'a') {
        const href = attrs.find((a) => a.name === 'href');
        const safe = href ? safeHref(href.value) : null;
        if (safe) {
            out.push(`href="${escapeAttr(safe.href)}"`);
            // Written INTO the stored markup rather than added at render time, so there
            // is one representation of a link and no seam where the rel could be
            // forgotten. External only: an in-app deep link should stay in the app.
            if (safe.external)
                out.push('target="_blank"', 'rel="noopener noreferrer nofollow"');
        }
    }
    if (tag === 'ol') {
        const start = attrs.find((a) => a.name === 'start');
        if (start && /^\d{1,6}$/.test(start.value.trim()))
            out.push(`start="${start.value.trim()}"`);
    }
    if (STYLEABLE.has(tag)) {
        const style = attrs.find((a) => a.name === 'style');
        const safe = style ? safeStyle(style.value) : null;
        if (safe)
            out.push(`style="${escapeAttr(safe)}"`);
    }
    return out.length ? ` ${out.join(' ')}` : '';
}
/**
 * One text block's content, made safe.
 *
 * Always returns well-formed markup: unclosed elements are closed, stray closing
 * tags are dropped, and both budgets stop the walk cleanly rather than mid-tag.
 * Never throws — a sanitizer that can fail on bad input is a denial of service on
 * the read path.
 */
export function sanitizeFeedbackRichText(raw) {
    if (!raw)
        return '';
    // A first hard bound on the WORK, before any parsing: past twice the markup
    // ceiling there is nothing to salvage, and the walk should not be asked to
    // prove it.
    const src = raw.length > FEEDBACK_TEXT_HTML_MAX * 2
        ? raw.slice(0, FEEDBACK_TEXT_HTML_MAX * 2)
        : raw;
    let out = '';
    let textLen = 0;
    const stack = [];
    /** The subtree currently being thrown away, and how deep into it we are. */
    let dropping = null;
    let dropDepth = 0;
    let i = 0;
    const budgetSpent = () => textLen >= FEEDBACK_TEXT_MAX || out.length >= FEEDBACK_TEXT_HTML_MAX;
    while (i < src.length) {
        if (budgetSpent())
            break;
        const lt = src.indexOf('<', i);
        // Trailing text, or text up to the next tag.
        const textEnd = lt < 0 ? src.length : lt;
        if (textEnd > i) {
            if (!dropping) {
                const decoded = decodeEntities(src.slice(i, textEnd));
                const room = FEEDBACK_TEXT_MAX - textLen;
                const kept = decoded.length > room ? decoded.slice(0, room) : decoded;
                textLen += kept.length;
                out += escapeText(kept);
            }
            i = textEnd;
        }
        if (lt < 0)
            break;
        const next = src.charAt(lt + 1);
        // A comment, a doctype, a CDATA section or a processing instruction. Dropped
        // whole. An UNTERMINATED comment eats the rest of the input, which is the
        // fail-closed direction: `<!--` with no `-->` is not a document.
        if (next === '!' || next === '?') {
            if (src.startsWith('<!--', lt)) {
                const end = src.indexOf('-->', lt + 4);
                i = end < 0 ? src.length : end + 3;
            }
            else {
                const end = src.indexOf('>', lt + 1);
                i = end < 0 ? src.length : end + 1;
            }
            continue;
        }
        const closing = next === '/';
        const nameAt = lt + (closing ? 2 : 1);
        const nameMatch = /^[a-zA-Z][a-zA-Z0-9:-]*/.exec(src.slice(nameAt, nameAt + 40));
        if (!nameMatch) {
            // A bare `<` in prose ("a < b"). It is TEXT, so it is escaped as text.
            if (!dropping && textLen < FEEDBACK_TEXT_MAX) {
                out += '&lt;';
                textLen += 1;
            }
            i = lt + 1;
            continue;
        }
        const tag = nameMatch[0].toLowerCase();
        const gt = tagEnd(src, nameAt + tag.length);
        if (gt < 0)
            break; // truncated tag: nothing more to trust
        const inner = src.slice(nameAt + tag.length, gt);
        i = gt + 1;
        // ── inside a dropped subtree ────────────────────────────────
        if (dropping) {
            if (tag === dropping) {
                if (closing) {
                    dropDepth -= 1;
                    if (dropDepth <= 0) {
                        dropping = null;
                        dropDepth = 0;
                    }
                }
                else if (!isSelfClosing(inner)) {
                    dropDepth += 1;
                }
            }
            continue;
        }
        if (closing) {
            if (!ALLOWED_TAGS.has(tag) || VOID_TAGS.has(tag))
                continue;
            // Close back to the matching open tag, so a `</ul>` that the writer put in
            // the wrong place closes the `<li>` still open inside it instead of being
            // dropped and leaving the list open for the rest of the block.
            const at = stack.lastIndexOf(tag);
            if (at < 0)
                continue; // a stray closer, closing nothing
            for (let d = stack.length - 1; d >= at; d--)
                out += `</${stack[d]}>`;
            stack.length = at;
            continue;
        }
        if (DROP_VOID.has(tag))
            continue;
        if (DROP_SUBTREE.has(tag)) {
            // Raw-text elements are skipped by SCANNING to their close tag, because
            // their contents are not markup and tokenising them would find tags that
            // are really text. Self-closing (`<svg/>`) needs no suppression at all.
            if (isSelfClosing(inner))
                continue;
            if (RAW_TEXT.has(tag)) {
                const close = src.toLowerCase().indexOf(`</${tag}`, i);
                if (close < 0) {
                    i = src.length;
                    continue;
                }
                const closeEnd = tagEnd(src, close + 2 + tag.length);
                i = closeEnd < 0 ? src.length : closeEnd + 1;
                continue;
            }
            dropping = tag;
            dropDepth = 1;
            continue;
        }
        // Anything merely unrecognised is UNWRAPPED: the tag goes, its children stay.
        // Losing a `<font>` or a `<table>` costs a wrapper; losing its text would cost
        // the writing.
        if (!ALLOWED_TAGS.has(tag))
            continue;
        const attrs = keepAttrs(tag, parseAttrs(inner));
        if (VOID_TAGS.has(tag)) {
            out += `<${tag}>`;
            continue;
        }
        // Self-closed non-void tag (`<span/>`, which a paste from an XML-ish source
        // produces): emit the pair rather than pushing an open tag nothing will close.
        if (isSelfClosing(inner)) {
            out += `<${tag}${attrs}></${tag}>`;
            continue;
        }
        if (stack.length >= MAX_DEPTH)
            continue; // too deep: unwrap, keep the text
        out += `<${tag}${attrs}>`;
        stack.push(tag);
    }
    // Close whatever is still open, innermost first. This is what makes the result
    // well-formed however the input ended — truncated, unbalanced or budget-stopped.
    for (let d = stack.length - 1; d >= 0; d--)
        out += `</${stack[d]}>`;
    return out;
}
/**
 * A block's content as plain text — what search, the AI brief and a clipboard's
 * `text/plain` want.
 *
 * Block-level tags become newlines and a `<li>` gets a bullet, so a pasted list
 * still reads as a list in a context that has no markup at all. Runs on the RAW
 * stored value: this is not a security boundary (the output is text), so it does
 * not need the sanitizer's walk.
 */
/** Tags that mark a LINE BOUNDARY in the plain projection. */
const PLAIN_STRUCTURAL = new Set([
    'p', 'div', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'blockquote', 'pre', 'table', 'thead', 'tbody', 'tr', 'td', 'th',
    'figure', 'figcaption', 'section', 'article',
]);
export function feedbackRichTextToPlain(raw) {
    if (!raw)
        return '';
    let out = '';
    /**
     * Newlines owed before the next piece of text.
     *
     * A block BOUNDARY and an explicit BREAK are counted differently on purpose.
     * `</p><p>` is ONE boundary between two paragraphs that merely happens to be
     * written as two tags, so a whole run of structural tags owes a single newline —
     * the obvious replace-every-tag-with-a-newline version double-spaced every
     * paragraph and put a blank line between every bullet. A `<br>` is a line break
     * somebody typed, so each one adds its own and two of them still make a blank
     * line.
     */
    let bound = 0;
    let breaks = 0;
    /** Set by an opening `<li>`, spent by the text that follows it. */
    let bullet = false;
    let i = 0;
    while (i < raw.length) {
        const lt = raw.indexOf('<', i);
        const textEnd = lt < 0 ? raw.length : lt;
        if (textEnd > i) {
            // Every kind of horizontal whitespace collapses, the non-breaking space
            // included — a contenteditable is full of them. Newlines in the SOURCE
            // collapse too: the line structure is the one thing this derives rather
            // than reads, so pretty-printed markup must not add breaks of its own.
            const text = decodeEntities(raw.slice(i, textEnd)).replace(/\s+/g, ' ');
            // Whitespace between two block tags is indentation, not content.
            if (text.trim() || !(bound || breaks)) {
                const owed = Math.min(2, bound + breaks);
                if (out)
                    out += '\n'.repeat(owed);
                bound = 0;
                breaks = 0;
                if (bullet) {
                    out += '• ';
                    bullet = false;
                }
                out += text;
            }
            i = textEnd;
        }
        if (lt < 0)
            break;
        const gt = raw.indexOf('>', lt + 1);
        if (gt < 0)
            break;
        const m = /^<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)/.exec(raw.slice(lt, gt + 1));
        i = gt + 1;
        if (!m)
            continue;
        const name = m[2].toLowerCase();
        if (name === 'br' || name === 'hr') {
            breaks += 1;
            continue;
        }
        if (!PLAIN_STRUCTURAL.has(name))
            continue;
        bound = 1;
        if (name === 'li' && !m[1])
            bullet = true;
    }
    return out
        .split('\n').map((l) => l.trim()).join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
/** How much TEXT a block holds, which is what FEEDBACK_TEXT_MAX bounds. Used by
 *  the editor's counter, so the number the user sees is the number the server
 *  enforces. */
export function feedbackRichTextLength(raw) {
    return feedbackRichTextToPlain(raw).length;
}
/** Whether a block has anything in it at all. `<p><br></p>` is what an emptied
 *  contenteditable leaves behind, and it is not content. */
export function isFeedbackRichTextEmpty(raw) {
    return feedbackRichTextToPlain(raw).length === 0;
}
/**
 * Plain text → the block's HTML, one paragraph per line.
 *
 * Used for a plain-text paste and for the `body` an MCP caller sends, so an agent
 * can put a text block on a board without composing markup. Escaped, so it cannot
 * smuggle any.
 */
export function feedbackPlainTextToRichText(text) {
    if (!text)
        return '';
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    return lines
        .map((l) => `<p>${l ? escapeText(l) : '<br>'}</p>`)
        .join('');
}
//# sourceMappingURL=feedback-text.js.map